import React, { useEffect, useState } from "react";
import { ArrowLeft, Trophy, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
// Note: updateUser, changePassword, checkNickname 함수는 프로젝트 환경에 따라 임포트 경로를 확인해주세요.
import { updateUser, changePassword, checkNickname } from "../../api/userApi";
import { useJWT } from "../../contexts/JWTContext";
import apiClient from "../../api/apiClient";
import debounce from "lodash.debounce";

import Toast from "../../components/common/Toast";
import { useToast } from "../../contexts/ToastContext";

function UpdateUser() {
  const navigate = useNavigate();
  const { userId, user, setUser } = useJWT();

  const { showToast } = useToast();
  const [avatarList, setAvatarList] = useState([]);

  const [selectedAvatar, setSelectedAvatar] = useState(1);
  const [nickname, setNickname] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const [errors, setErrors] = useState({});
  const [successMessages, setSuccessMessages] = useState({});
  const [focusedField, setFocusedField] = useState("");

  // 아바타 맵
  const avatarMap = {
    1: "avatar_basic",
    2: "avatar_runner",
    3: "avatar_pro",
    4: "avatar_master",
  };

  useEffect(() => {
    if (!user) return;

    setNickname(user.nickname);
    setSelectedAvatar(user.avatarId);
  }, [user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!userId) {
        navigate("/login");
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userId, navigate]);

  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        const res = await apiClient.get("/user/avatar/list");
        setAvatarList(res.data);
      } catch (err) {
        console.error("아바타 목록 조회 실패", err);
        showToast(
          "아바타 목록을 불러오지 못했어요. 잠시 후 다시 시도해주세요.",
          "e"
        );
      }
    };

    fetchAvatars();
  }, [showToast]);

  // ========================
  // 유효성 검사 공통 함수들
  // ========================

  // 비밀번호 유효성 검사
  const validateNewPasswordFull = (password) => {
    if (!password) return "새 비밀번호를 입력해주세요";
    if (password.length < 4 || password.length > 12)
      return "비밀번호는 4~12자여야 합니다";

    const types = [
      /[a-zA-Z]/.test(password),
      /[0-9]/.test(password),
      /[^a-zA-Z0-9]/.test(password),
    ];
    const typeCount = types.filter(Boolean).length;

    if (typeCount < 2)
      return "영문, 숫자, 특수문자 중 최소 2개를 포함해야 합니다";

    return "";
  };

  // 실시간 닉네임 중복 검사 (서버)
  const debouncedCheckNickname = debounce(async (nickname) => {
    if (!nickname || nickname.length < 1 || nickname.length > 10) return;

    // 만약 현재 닉네임과 같다면 중복 검사 스킵
    if (user && user.nickname === nickname) {
      setErrors((prev) => ({ ...prev, nickname: "" }));
      setSuccessMessages((prev) => ({ ...prev, nickname: "" }));
      return;
    }

    try {
      const isDuplicate = await checkNickname(nickname);

      if (isDuplicate) {
        setErrors((prev) => ({
          ...prev,
          nickname: "이미 사용 중인 닉네임 입니다.",
        }));
        setSuccessMessages((prev) => ({ ...prev, nickname: "" }));
      } else {
        setErrors((prev) => ({ ...prev, nickname: "" }));
        setSuccessMessages((prev) => ({
          ...prev,
          nickname: "사용 가능한 닉네임 입니다.",
        }));
      }
    } catch (e) {
      console.error("닉네임 중복 확인 오류:", e);
      showToast(
        "닉네임 중복 확인에 실패했어요. 네트워크 상태를 확인해주세요.",
        "e"
      );
    }
  }, 400);

  // 실시간 필드별 유효성 검사 (화면 표시용)
  const validateField = (name, value) => {
    let error = "";
    let success = "";

    // 1. 새 비밀번호
    if (name === "newPassword") {
      if (!value) {
        error = "새 비밀번호를 입력해주세요";
      } else if (value.length < 4 || value.length > 12) {
        error = "비밀번호는 4~12자여야 합니다";
      } else {
        const types = [
          /[a-zA-Z]/.test(value),
          /[0-9]/.test(value),
          /[^a-zA-Z0-9]/.test(value),
        ];
        const typeCount = types.filter(Boolean).length;
        if (typeCount < 2) {
          error = "영문, 숫자, 특수문자 중 최소 2개를 포함해야 합니다";
        }
      }
    }

    // 2. 비밀번호 확인
    if (name === "confirmPassword" && (newPassword || value)) {
      if (!value) {
        error = "비밀번호 확인을 입력해주세요";
      } else if (newPassword !== value) {
        error = "비밀번호가 일치하지 않습니다";
      } else {
        success = "비밀번호가 일치합니다.";
      }
    }

    // 3. 닉네임 검사
    if (name === "nickname") {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        error = "닉네임을 입력해주세요";
      } else if (trimmedValue.length < 1 || trimmedValue.length > 10) {
        error = "닉네임은 1~10자여야 합니다";
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    setSuccessMessages((prev) => ({ ...prev, [name]: success }));
  };

  // 저장 처리
  const handleSave = async () => {
    setErrors({});

    try {
      const trimmedNickname = nickname.trim();

      const originalNickname = user?.nickname ?? "";
      const originalAvatarId = user?.avatarId ?? 1;

      const profileChanged =
        trimmedNickname !== originalNickname ||
        selectedAvatar !== originalAvatarId;

      const wantsPwChange =
        !!currentPassword.trim() ||
        !!newPassword.trim() ||
        !!confirmPassword.trim();

      if (!profileChanged && !wantsPwChange) {
        showToast("변경된 내용이 없어요.", "i");
        return;
      }

      if (profileChanged) {
        if (!trimmedNickname) {
          setErrors((prev) => ({ ...prev, nickname: "닉네임을 입력해주세요" }));
          showToast("닉네임을 확인해주세요.", "w");
          return;
        }
        if (trimmedNickname.length < 1 || trimmedNickname.length > 10) {
          setErrors((prev) => ({
            ...prev,
            nickname: "닉네임은 1~10자여야 합니다",
          }));
          showToast("닉네임을 확인해주세요.", "w");
          return;
        }

        if (errors.nickname) {
          showToast("닉네임을 확인해주세요.", "w");
          return;
        }

        if (trimmedNickname !== originalNickname) {
          const isDuplicate = await checkNickname(trimmedNickname);
          if (isDuplicate) {
            setErrors((prev) => ({
              ...prev,
              nickname: "이미 사용 중인 닉네임 입니다.",
            }));
            showToast("이미 사용 중인 닉네임이에요.", "w");
            return;
          }
        }
      }

      if (wantsPwChange) {
        if (!currentPassword.trim()) {
          setErrors((prev) => ({
            ...prev,
            currentPassword: "현재 비밀번호를 입력해주세요",
          }));
          showToast(
            "현재 비밀번호를 입력해야 비밀번호를 변경할 수 있습니다.",
            "w"
          );
          return;
        }

        const pwError = validateNewPasswordFull(newPassword);
        if (pwError) {
          setErrors((prev) => ({ ...prev, newPassword: pwError }));
          showToast("새 비밀번호 형식을 확인해 주세요.", "w");
          return;
        }

        if (!confirmPassword || newPassword !== confirmPassword) {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: "비밀번호가 일치하지 않습니다",
          }));
          showToast("새 비밀번호 확인이 일치하지 않아요.", "w");
          return;
        }
      }

      let profileOk = false;
      let pwOk = false;

      if (profileChanged) {
        await updateUser({
          // userId, // 백엔드에서 JWT로 userId 추출하므로 불필요, 절대 사용 금지 X
          nickname: trimmedNickname,
          avatarId: selectedAvatar,
        });
        setUser((prev) =>
          prev
            ? { ...prev, nickname: trimmedNickname, avatarId: selectedAvatar }
            : prev
        );
        profileOk = true;
      }

      if (wantsPwChange) {
        await changePassword({
          // userId, // 백엔드에서 JWT로 userId 추출하므로 불필요, 절대 사용 금지 X
          currentPassword,
          newPassword,
        });
        pwOk = true;
      }

      if (profileOk && pwOk) showToast("프로필과 비밀번호를 변경했어요.", "i");
      else if (profileOk) showToast("프로필을 변경했어요.", "i");
      else if (pwOk) showToast("비밀번호를 변경했어요.", "i");
      else showToast("변경된 내용이 없어요.", "");

      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      console.error("저장 오류:", err.response || err);

      const response = err.response;

      if (!response) {
        showToast(
          "네트워크 연결이 불안정해요. 인터넷을 확인하고 다시 시도해주세요.",
          "e"
        );
        return;
      }

      // 현재 비밀번호 불일치
      if (response.status === 401 || response.status === 400) {
        const serverErrorMessage =
          (typeof response.data === "string"
            ? response.data
            : response.data?.message) || "";

        if (
          serverErrorMessage.includes("현재 비밀번호") ||
          serverErrorMessage.includes("mismatch")
        ) {
          setErrors((prev) => ({
            ...prev,
            currentPassword: "현재 비밀번호가 일치하지 않습니다.",
          }));
          showToast("현재 비밀번호가 올바르지 않아요.", "e");
          return;
        }

        if (
          serverErrorMessage.includes("닉네임") ||
          serverErrorMessage.includes("nickname")
        ) {
          setErrors((prev) => ({
            ...prev,
            nickname: "닉네임을 확인해주세요.",
          }));
          showToast("닉네임을 확인해주세요.", "e");
          return;
        }
      }

      if (response.status >= 500) {
        showToast(
          "서버 오류로 저장에 실패했어요. 잠시 후 다시 시도해주세요.",
          "e"
        );
        return;
      }

      showToast(
        "프로필 저장에 실패했어요. 입력값을 확인하고 다시 시도해주세요.",
        "e"
      );
    }
  };

  const handleBack = () => navigate(-1);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white px-4 py-6">
      <Toast />

      {/* HEADER */}
      <header className="flex items-center justify-between mb-6">
        <button onClick={handleBack} className="w-8 h-8">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">프로필 수정</h1>
        <div className="w-8"></div>
      </header>

      {/* 프로필 미리보기 */}
      <section className="bg-white rounded-3xl shadow-lg p-6 mb-5">
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 rounded-full overflow-hidden shadow-lg mb-4">
            <img
              src={`/images/avatar/${avatarMap[selectedAvatar]}.png`}
              alt="avatar"
              className="w-full h-full object-contain scale-150"
            />
          </div>

          <input
            value={nickname}
            onChange={(e) => {
              const value = e.target.value;
              setNickname(value);
              validateField("nickname", value);
              if (value.trim().length >= 1 && value.trim().length <= 10) {
                debouncedCheckNickname(value.trim());
              }
            }}
            onFocus={() => setFocusedField("nickname")}
            onBlur={() => setFocusedField("")}
            className={`text-xl font-bold text-center border-2 px-4 py-2 rounded-xl transition-all focus:outline-none
                ${
                  errors.nickname
                    ? "border-red-400 bg-red-50"
                    : focusedField === "nickname"
                    ? "border-teal-400 shadow-lg shadow-teal-100"
                    : "border-gray-200 hover:border-gray-300"
                }
            `}
          />
          {errors.nickname ? (
            <p className="text-sm text-red-500 mt-2">{errors.nickname}</p>
          ) : successMessages.nickname ? (
            <p className="text-sm mt-2 font-medium text-teal-500">
              {successMessages.nickname}
            </p>
          ) : null}
        </div>
      </section>

      {/* 프로필 사진 선택 */}
      <section className="bg-white rounded-3xl shadow-lg p-6 mb-5 border-2 border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-bold text-gray-800">아바타 선택</h3>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {avatarList.map((avatar) => {
            const unlocked = user.levelId >= avatar.reqLevel;
            const selected = selectedAvatar === avatar.avatarId;

            return (
              <button
                key={avatar.avatarId}
                onClick={() => unlocked && setSelectedAvatar(avatar.avatarId)}
                disabled={!unlocked}
                className={`relative w-16 h-16 rounded-2xl overflow-hidden shadow-md transition-all
                  ${unlocked ? "" : "opacity-30"}
                  ${selected ? "ring-4 ring-[rgb(0,224,198)] scale-110" : ""}
                `}
              >
                <img
                  src={`/images/avatar/${avatar.avatarImg}`}
                  alt="avatar"
                  className="w-full h-full object-contain scale-[1.8]"
                />

                {!unlocked && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <span className="text-xl">🔒</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* 비밀번호 변경 */}
      <section className="bg-white rounded-3xl shadow-lg p-6 mb-5">
        <h3 className="font-bold mb-4">비밀번호 변경</h3>

        {/* 현재 비밀번호 */}
        <div className="mb-4">
          <div className="relative">
            {" "}
            {/* input과 button을 감싸는 relative div */}
            <input
              type={showCurrentPw ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setErrors((prev) => ({ ...prev, currentPassword: "" }));
              }}
              className="w-full px-4 py-3 border-2 rounded-xl"
              placeholder="현재 비밀번호"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPw(!showCurrentPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              {showCurrentPw ? <EyeOff /> : <Eye />}
            </button>
          </div>
          {errors.currentPassword && (
            <p className="text-sm text-red-500 mt-2">
              {errors.currentPassword}
            </p>
          )}
        </div>

        {/* 새 비밀번호 */}
        <div className="mb-4">
          <div className="relative">
            {" "}
            {/* input과 button을 감싸는 relative div */}
            <input
              type={showNewPw ? "text" : "password"}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                validateField("newPassword", e.target.value);
              }}
              onFocus={() => setFocusedField("newPassword")}
              onBlur={() => setFocusedField("")}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all
                  ${
                    errors.newPassword
                      ? "border-red-400 bg-red-50"
                      : focusedField === "newPassword"
                      ? "border-teal-400 shadow-lg shadow-teal-100"
                      : "border-gray-200 hover:border-gray-300"
                  }
              `}
              placeholder="새 비밀번호"
            />
            <button
              type="button"
              onClick={() => setShowNewPw(!showNewPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              {showNewPw ? <EyeOff /> : <Eye />}
            </button>
          </div>
          {errors.newPassword && (
            <p className="text-sm text-red-500 mt-2">{errors.newPassword}</p>
          )}
          {successMessages.newPassword && (
            <p className="text-sm text-teal-500 mt-2">
              {successMessages.newPassword}
            </p>
          )}
        </div>

        {/* 새 비밀번호 확인 */}
        <div>
          <div className="relative">
            {" "}
            {/* input과 button을 감싸는 relative div */}
            <input
              type={showConfirmPw ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                validateField("confirmPassword", e.target.value);
              }}
              onFocus={() => setFocusedField("confirmPassword")}
              onBlur={() => setFocusedField("")}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all
                  ${
                    errors.confirmPassword
                      ? "border-red-400 bg-red-50"
                      : successMessages.confirmPassword
                      ? "border-teal-400"
                      : "border-gray-200 hover:border-gray-300"
                  }
              `}
              placeholder="새 비밀번호 확인"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPw(!showConfirmPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              {showConfirmPw ? <EyeOff /> : <Eye />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-sm text-red-500 mt-2">
              {errors.confirmPassword}
            </p>
          )}
          {successMessages.confirmPassword && (
            <p className="text-sm text-teal-500 mt-2">
              {successMessages.confirmPassword}
            </p>
          )}
        </div>
      </section>

      {/* 저장 버튼 */}
      <button
        onClick={handleSave}
        className="w-full py-4 rounded-2xl font-bold text-white shadow-lg mb-24 bg-teal-400"
      >
        저장하기
      </button>
    </div>
  );
}

export default UpdateUser;
